import { CSSProperties, MouseEvent } from 'react';
import Ellipsis from 'react-ellipsis-pjs';
import styled from 'styled-components';

import { Card } from 'app/theme/Card';
import { DeleteIcon } from 'app/theme/Icons/DeleteIcon';
import { DocIcon } from 'app/theme/Icons/DocIcon';
import { getFormattedFileName, UploadedFile } from 'pkg/components/FileUploader/UploadedFile';

interface FileCardProps {
  uploadedFile: Pick<UploadedFile, 'progress' | 'file'>;
  onRemove: () => void;
}

const StyledCard = styled(Card)`
  &&& {
    margin: 0;
    height: 9.5rem;
    position: relative;
    padding: 0;

    &:hover {
      transform: none;
    }
  }
`;

const StyledContent = styled(Card.Content)`
  &&& {
    padding: 1rem 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;

    &::after {
      content: none;
    }
  }
`;

const Description = styled(Card.Description)`
  font-size: 1.2rem;
  word-break: break-all;
  line-height: 1.4rem;
  max-height: 2.8rem;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  width: 80%;
  overflow: hidden;
  position: relative;
  margin: 0 auto;
  height: 0.5rem;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.palette.gray.dividerGray};
  transition: all 0.3s;

  &::after {
    transition: width 0.2s;
    border-radius: 0.5rem;
    height: 100%;
    top: 0;
    left: 0;
    position: absolute;
    content: '  ';
    width: ${({ $progress }) => $progress}%;
    background-color: ${({ theme }) => theme.palette.progressBar};
  }
`;

const RemoveIcon = styled(DeleteIcon)`
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  cursor: pointer;
`;

const StyledDocIcon = styled(DocIcon)`
  display: block;
  margin: 0 auto;
`;

const InvisibleButton = styled.button`
  background: none;
  border: none;
  margin: 0;
  padding: 0;
`;

// Ellipsis is not compatible with styled-components, so AFAIK this is only way to center text inside.
const centeredTextStyle: CSSProperties = { textAlign: 'center' };

export function FileCard({ uploadedFile, onRemove }: FileCardProps): JSX.Element {
  const { progress, file } = uploadedFile;
  const fileName = getFormattedFileName(file.name);
  return (
    <StyledCard onClick={(event: MouseEvent) => event.stopPropagation()}>
      <StyledContent>
        <StyledDocIcon />
        <Description>
          <Ellipsis style={centeredTextStyle} text={fileName} lines={2} />
        </Description>
        <span data-testid={progress === 100 ? 'uploaded' : 'not-uploaded'}>
          <ProgressBar $progress={progress} />
        </span>
      </StyledContent>
      <InvisibleButton type="button" aria-label={`Remove file ${fileName}`} onClick={onRemove}>
        <RemoveIcon />
      </InvisibleButton>
    </StyledCard>
  );
}
